import React, { useState } from 'react';
import { WorksheetNote } from '../../types/worksheet';
import {
  ChatBubbleLeftRightIcon,
  EyeIcon,
  LockClosedIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { useLanguage } from '../../contexts/LanguageContext';

interface WorksheetNotesProps {
  notes: WorksheetNote[];
  onAdd: (text: string, visibility: 'customer' | 'internal') => void;
  onRemove: (noteId: string) => void;
  readOnly?: boolean;
}

const WorksheetNotes: React.FC<WorksheetNotesProps> = ({ notes, onAdd, onRemove, readOnly = false }) => {
  const { t } = useLanguage();
  const [text, setText] = useState('');
  const [visibility, setVisibility] = useState<'customer' | 'internal'>('internal');

  const handleAdd = () => {
    if (!text.trim()) return;
    onAdd(text.trim(), visibility);
    setText('');
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-3 py-2 sm:px-4 sm:py-3 border-b border-gray-200 dark:border-gray-700 flex items-center space-x-2 bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-700 dark:to-purple-700">
        <ChatBubbleLeftRightIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        <h3 className="text-xs sm:text-sm font-semibold text-white">{t('worksheets.notes.title')}</h3>
        <span className="text-[10px] sm:text-xs text-white/80">({notes.length})</span>
      </div>

      {/* Notes list */}
      {notes.length > 0 && (
        <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-48 overflow-y-auto">
          {notes.map((note) => {
            const created = note.createdAt instanceof Date ? note.createdAt : new Date(note.createdAt as any);
            return (
              <div key={note.id} className="px-3 py-1.5 sm:px-4 sm:py-2 group hover:bg-gray-50 dark:hover:bg-gray-700/30">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1 flex-wrap">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-medium ${
                        note.visibility === 'customer'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {note.visibility === 'customer' ? (
                          <><EyeIcon className="w-3 h-3 mr-0.5" /> {t('worksheets.notes.customer')}</>
                        ) : (
                          <><LockClosedIcon className="w-3 h-3 mr-0.5" /> {t('worksheets.notes.internal')}</>
                        )}
                      </span>
                      <span className="text-[10px] sm:text-xs text-gray-400">{note.createdByName}</span>
                      <span className="text-[10px] sm:text-xs text-gray-400">{format(created, 'MM.dd HH:mm', { locale: hu })}</span>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{note.text}</p>
                  </div>
                  {!readOnly && (
                    <button
                      onClick={() => onRemove(note.id)}
                      className="p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <TrashIcon className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add note */}
      {!readOnly && (
        <div className="px-3 py-2 sm:px-4 sm:py-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setVisibility('internal')}
              className={`inline-flex items-center px-2 py-1 text-[10px] sm:text-xs font-medium rounded ${
                visibility === 'internal'
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <LockClosedIcon className="w-3 h-3 mr-1" /> {t('worksheets.notes.internal')}
            </button>
            <button
              onClick={() => setVisibility('customer')}
              className={`inline-flex items-center px-2 py-1 text-[10px] sm:text-xs font-medium rounded ${
                visibility === 'customer'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <EyeIcon className="w-3 h-3 mr-1" /> {t('worksheets.notes.customer')}
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={t('worksheets.notes.placeholder')}
              rows={2}
              className="crm-input-raw flex-1 min-w-0 text-sm leading-5 border border-[var(--crm-border)] rounded-md px-2 py-1.5 bg-[var(--crm-surface)] text-gray-700 dark:text-gray-200 resize-none focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <button
              onClick={handleAdd}
              className="h-7 px-2.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 self-end flex-shrink-0"
            >
              {t('worksheets.actions.add')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorksheetNotes;
